import { createSpecificActivityOccupationApi, updateSpecificActivityOccupationApi } from "@/api/services/global/master";
import { TGetSpecificActivityOccupationListOutputDto } from "@/api/services/global/master/dtos/get-master-specific-activity-occupation-list.dto";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { masterDataSpecificActivityOccupationSchema } from "@/features/master-data/schema/masterDataSpecificActivityOccupationSchema";
import { TMasterDataSpecificActivityOccupationForm } from "@/features/master-data/types/masterDataSpecificActivityOccupationForm";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";

type TSpecificActivityOccupationFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
  selectedSpecificActivityOccupation?: TGetSpecificActivityOccupationListOutputDto;
};

export const SpecificActivityOccupationFormModal: FC<TSpecificActivityOccupationFormModalProps> = ({
  onClose,
  isOpen,
  selectedSpecificActivityOccupation,
}) => {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(
    () => ({
      label: selectedSpecificActivityOccupation ? selectedSpecificActivityOccupation.label : undefined,
      csvCode: selectedSpecificActivityOccupation ? selectedSpecificActivityOccupation.csvCode : undefined,
    }),
    [selectedSpecificActivityOccupation],
  );

  const methods = useForm<TMasterDataSpecificActivityOccupationForm>({
    resolver: yupResolver(masterDataSpecificActivityOccupationSchema),
    defaultValues,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const { control } = methods;

  const onSubmit = async (data: TMasterDataSpecificActivityOccupationForm) => {
    try {
      if (selectedSpecificActivityOccupation) {
        const rs = await updateSpecificActivityOccupationApi({
          specificActivityOccupations: [
            {
              id: toNumber(selectedSpecificActivityOccupation.id),
              label: data.label,
              csvCode: data.csvCode,
            },
          ],
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createSpecificActivityOccupationApi({
          label: data.label,
          csvCode: data.csvCode,
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_SPECIFIC_ACTIVITY_OCCUPATION] });
      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal
      title={selectedSpecificActivityOccupation ? "編集" : "追加"}
      isOpen={isOpen}
      enableFooter={false}
      onClose={onClose}
      enableCloseIcon={false}
    >
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataSpecificActivityOccupationSchema}>
        <Stack gap={3} my={3}>
          <Grid item xs={6}>
            <FormField control={control} name="label">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="csvCode">
              <Input />
            </FormField>
          </Grid>
        </Stack>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit">保存</Button>
        </Stack>
      </Form>
    </Modal>
  );
};
